import apiClient from './apiClient';
import {
  Organization,
  OrganizationSettings,
  OrganizationMetrics,
  OrganizationMember,
  OrganizationApiKey,
  CrossOrgShareRequest,
  PatientTransfer,
  OrganizationTemplate,
} from '../types';

// Helper to extract data from response (handles both wrapped and unwrapped responses)
const extractData = <T>(responseData: any): T => {
  // If response has a 'data' property, use it (wrapped format)
  if (responseData && typeof responseData === 'object' && 'data' in responseData) {
    return responseData.data;
  }
  // Otherwise return as-is (direct format)
  return responseData;
};

// Organization Management
export const getOrganizations = async (): Promise<Organization[]> => {
  const response = await apiClient.get('/api/organizations');
  return extractData<Organization[]>(response.data) || [];
};

export const getOrganization = async (id: string): Promise<Organization> => {
  const response = await apiClient.get(`/api/organizations/${id}`);
  return extractData<Organization>(response.data);
};

export const createOrganization = async (data: Partial<Organization>): Promise<Organization> => {
  const response = await apiClient.post('/api/organizations', data);
  return extractData<Organization>(response.data);
};

export const updateOrganization = async (id: string, data: Partial<Organization>): Promise<Organization> => {
  const response = await apiClient.put(`/api/organizations/${id}`, data);
  return extractData<Organization>(response.data);
};

export const getOrganizationUsage = async (id: string): Promise<{
  currentPatients: number;
  currentUsers: number;
  currentStorageGB: number;
  limits: {
    maxPatients: number;
    maxUsers: number;
    maxStorageGB: number;
  };
}> => {
  const response = await apiClient.get(`/api/organizations/${id}/usage`);
  return extractData(response.data);
};

export const getOrganizationChildren = async (id: string): Promise<Organization[]> => {
  const response = await apiClient.get(`/api/organizations/${id}/children`);
  return extractData<Organization[]>(response.data) || [];
};

export const getOrganizationParent = async (id: string): Promise<Organization | null> => {
  const response = await apiClient.get(`/api/organizations/${id}/parent`);
  return extractData<Organization>(response.data) || null;
};

export const getOrganizationHierarchy = async (id: string): Promise<{
  organization: Organization;
  parent?: Organization;
  children: Organization[];
}> => {
  const response = await apiClient.get(`/api/organizations/${id}/hierarchy`);
  return extractData(response.data);
};

export const setOrganizationParent = async (id: string, parentId: string): Promise<void> => {
  await apiClient.put(`/api/organizations/${id}/parent`, { parentId });
};

// Organization Settings
export const getOrganizationSettings = async (organizationId: string): Promise<OrganizationSettings> => {
  const response = await apiClient.get(`/api/organizations/${organizationId}/settings`);
  return extractData<OrganizationSettings>(response.data);
};

export const updateOrganizationSettings = async (
  organizationId: string,
  data: Partial<OrganizationSettings>
): Promise<OrganizationSettings> => {
  const response = await apiClient.put(`/api/organizations/${organizationId}/settings`, data);
  return extractData<OrganizationSettings>(response.data);
};

// Organization Metrics
export const getOrganizationMetrics = async (
  organizationId: string,
  month?: string
): Promise<OrganizationMetrics[]> => {
  const params = month ? { month } : {};
  const response = await apiClient.get(`/api/organizations/${organizationId}/metrics`, { params });
  return extractData<OrganizationMetrics[]>(response.data) || [];
};

export const getOrganizationMetricsForMonth = async (
  organizationId: string,
  month: string
): Promise<OrganizationMetrics> => {
  const response = await apiClient.get(`/api/organizations/${organizationId}/metrics/${month}`);
  return extractData<OrganizationMetrics>(response.data);
};

// Organization Members
export const getOrganizationMembers = async (organizationId: string): Promise<OrganizationMember[]> => {
  const response = await apiClient.get(`/api/organizations/${organizationId}/members`);
  const data = extractData<any>(response.data);
  // Handle paginated response
  return data?.members || data || [];
};

export const getOrganizationMember = async (
  organizationId: string,
  userId: string
): Promise<OrganizationMember> => {
  const response = await apiClient.get(`/api/organizations/${organizationId}/members/${userId}`);
  return extractData<OrganizationMember>(response.data);
};

export const addOrganizationMember = async (
  organizationId: string,
  data: { userId: string; role: string; firstName?: string; lastName?: string; email?: string }
): Promise<OrganizationMember> => {
  const response = await apiClient.post(`/api/organizations/${organizationId}/members`, data);
  return extractData<OrganizationMember>(response.data);
};

export const updateOrganizationMember = async (
  organizationId: string,
  userId: string,
  data: { role: string }
): Promise<OrganizationMember> => {
  const response = await apiClient.put(`/api/organizations/${organizationId}/members/${userId}/role`, data);
  return extractData<OrganizationMember>(response.data);
};

export const removeOrganizationMember = async (organizationId: string, userId: string): Promise<void> => {
  await apiClient.delete(`/api/organizations/${organizationId}/members/${userId}`);
};

export const setMemberPrimaryOrganization = async (
  organizationId: string,
  userId: string
): Promise<void> => {
  await apiClient.put(`/api/organizations/${organizationId}/members/${userId}/set-primary`);
};

export const getUserOrganizations = async (userId: string): Promise<OrganizationMember[]> => {
  const response = await apiClient.get(`/api/organizations/user/${userId}/organizations`);
  return extractData<OrganizationMember[]>(response.data) || [];
};

// Organization API Keys
export const getOrganizationApiKeys = async (organizationId: string): Promise<OrganizationApiKey[]> => {
  const response = await apiClient.get(`/api/organizations/${organizationId}/api-keys`);
  return extractData<OrganizationApiKey[]>(response.data) || [];
};

export const getOrganizationApiKey = async (
  organizationId: string,
  keyId: string
): Promise<OrganizationApiKey> => {
  const response = await apiClient.get(`/api/organizations/${organizationId}/api-keys/${keyId}`);
  return extractData<OrganizationApiKey>(response.data);
};

export const createOrganizationApiKey = async (
  organizationId: string,
  data: { keyName: string; expiryDate?: string }
): Promise<{ apiKey: OrganizationApiKey; keyValue: string }> => {
  const response = await apiClient.post(`/api/organizations/${organizationId}/api-keys`, {
    name: data.keyName,
    expiresAt: data.expiryDate,
    permissions: ['read', 'write'],
  });
  const result = extractData<any>(response.data);
  // Map backend response to expected format
  return {
    apiKey: result as OrganizationApiKey,
    keyValue: result?.plainKey || result?.keyValue || '',
  };
};

export const revokeOrganizationApiKey = async (organizationId: string, keyId: string): Promise<void> => {
  await apiClient.delete(`/api/organizations/${organizationId}/api-keys/${keyId}`);
};

export const getApiKeyUsageStats = async (
  organizationId: string,
  keyId: string
): Promise<{ usageCount: number; lastUsedAt?: string }> => {
  const response = await apiClient.get(`/api/organizations/${organizationId}/api-keys/${keyId}/usage`);
  return extractData(response.data);
};

// Cross-Organization Sharing
export const getOutgoingShareRequests = async (organizationId: string): Promise<CrossOrgShareRequest[]> => {
  const response = await apiClient.get(`/api/organizations/${organizationId}/sharing/outgoing`);
  return extractData<CrossOrgShareRequest[]>(response.data) || [];
};

export const getIncomingShareRequests = async (organizationId: string): Promise<CrossOrgShareRequest[]> => {
  const response = await apiClient.get(`/api/organizations/${organizationId}/sharing/incoming`);
  return extractData<CrossOrgShareRequest[]>(response.data) || [];
};

export const getActiveShares = async (organizationId: string): Promise<CrossOrgShareRequest[]> => {
  const response = await apiClient.get(`/api/organizations/${organizationId}/sharing/active`);
  return extractData<CrossOrgShareRequest[]>(response.data) || [];
};

export const createShareRequest = async (
  organizationId: string,
  data: {
    toOrganizationId: string;
    patientId: string;
    requestedDataTypes: string[];
    purpose: string;
    expiryDate?: string;
  }
): Promise<CrossOrgShareRequest> => {
  const response = await apiClient.post(`/api/organizations/${organizationId}/sharing/requests`, data);
  return extractData<CrossOrgShareRequest>(response.data);
};

export const approveShareRequest = async (organizationId: string, requestId: string): Promise<void> => {
  await apiClient.put(`/api/organizations/${organizationId}/sharing/requests/${requestId}/approve`);
};

export const denyShareRequest = async (
  organizationId: string,
  requestId: string,
  reason: string
): Promise<void> => {
  await apiClient.put(`/api/organizations/${organizationId}/sharing/requests/${requestId}/deny`, {
    reason,
  });
};

export const revokeShare = async (organizationId: string, requestId: string): Promise<void> => {
  await apiClient.delete(`/api/organizations/${organizationId}/sharing/requests/${requestId}`);
};

export const validateShareAccess = async (
  organizationId: string,
  patientId: string,
  dataType: string
): Promise<{ hasAccess: boolean; shareRequest?: CrossOrgShareRequest }> => {
  const response = await apiClient.get(`/api/organizations/${organizationId}/sharing/validate`, {
    params: { patientId, dataType },
  });
  return extractData(response.data);
};

// Patient Transfers
export const getOutgoingTransfers = async (organizationId: string): Promise<PatientTransfer[]> => {
  const response = await apiClient.get(`/api/organizations/${organizationId}/transfers/outgoing`);
  return extractData<PatientTransfer[]>(response.data) || [];
};

export const getIncomingTransfers = async (organizationId: string): Promise<PatientTransfer[]> => {
  const response = await apiClient.get(`/api/organizations/${organizationId}/transfers/incoming`);
  return extractData<PatientTransfer[]>(response.data) || [];
};

export const getPendingTransfers = async (organizationId: string): Promise<PatientTransfer[]> => {
  const response = await apiClient.get(`/api/organizations/${organizationId}/transfers/pending`);
  return extractData<PatientTransfer[]>(response.data) || [];
};

export const getPatientTransfers = async (patientId: string): Promise<PatientTransfer[]> => {
  const response = await apiClient.get(`/api/patient-transfers/patient/${patientId}`);
  return extractData<PatientTransfer[]>(response.data) || [];
};

export const createPatientTransfer = async (data: {
  patientId: string;
  fromOrganizationId: string;
  toOrganizationId: string;
  reason: string;
}): Promise<PatientTransfer> => {
  const response = await apiClient.post('/api/patient-transfers', data);
  return extractData<PatientTransfer>(response.data);
};

export const approveTransferByFromOrg = async (transferId: string): Promise<void> => {
  await apiClient.put(`/api/patient-transfers/${transferId}/approve-from-org`);
};

export const approveTransferByToOrg = async (transferId: string): Promise<void> => {
  await apiClient.put(`/api/patient-transfers/${transferId}/approve-to-org`);
};

export const completeTransfer = async (transferId: string): Promise<void> => {
  await apiClient.put(`/api/patient-transfers/${transferId}/complete`);
};

export const cancelTransfer = async (transferId: string): Promise<void> => {
  await apiClient.put(`/api/patient-transfers/${transferId}/cancel`);
};

export const denyTransfer = async (transferId: string, reason: string): Promise<void> => {
  await apiClient.put(`/api/patient-transfers/${transferId}/deny`, { reason });
};

// Organization Templates
export const getAllTemplates = async (): Promise<OrganizationTemplate[]> => {
  const response = await apiClient.get('/api/organization-templates');
  return extractData<OrganizationTemplate[]>(response.data) || [];
};

export const getTemplate = async (id: string): Promise<OrganizationTemplate> => {
  const response = await apiClient.get(`/api/organization-templates/${id}`);
  return extractData<OrganizationTemplate>(response.data);
};

export const createTemplate = async (data: Partial<OrganizationTemplate>): Promise<OrganizationTemplate> => {
  const response = await apiClient.post('/api/organization-templates', data);
  return extractData<OrganizationTemplate>(response.data);
};

export const updateTemplate = async (
  id: string,
  data: Partial<OrganizationTemplate>
): Promise<OrganizationTemplate> => {
  const response = await apiClient.put(`/api/organization-templates/${id}`, data);
  return extractData<OrganizationTemplate>(response.data);
};

export const deleteTemplate = async (id: string): Promise<void> => {
  await apiClient.delete(`/api/organization-templates/${id}`);
};

export const createOrganizationFromTemplate = async (
  templateId: string,
  data: {
    name: string;
    contactEmail: string;
    contactPhone: string;
    address: any;
  }
): Promise<Organization> => {
  const response = await apiClient.post(`/api/organization-templates/${templateId}/create-organization`, data);
  return extractData<Organization>(response.data);
};
